<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import ClinicalCrmAPI from 'dashboard/api/clinicalCrm';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import {
  isOpportunityOverdue,
  toDatetimeLocal,
} from 'dashboard/routes/dashboard/clinicalCrm/workspace';
import { stageColorClasses } from 'dashboard/routes/dashboard/clinicalCrm/stagePalette';

const props = defineProps({
  contactId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const opportunity = ref(null);
const procedures = ref([]);
const isLoading = ref(false);
const agents = useMapGetter('agents/getAgents');
// Free-text and date fields save on blur, so the last persisted pair is kept
// to tell a real edit from a focus that merely passed through. Without it every
// tab keystroke would fire a PATCH and an "updated" toast.
const savedFollowUp = ref({ nextAction: '', nextActionAt: '' });

const isOverdue = computed(() =>
  opportunity.value ? isOpportunityOverdue(opportunity.value) : false
);

const campaignLabel = computed(() => {
  const record = opportunity.value;
  if (!record || record.sourceType !== 'meta') return '';
  return record.metaHeadline || record.metaAdId || '';
});

const stageDefinitions = computed(() => [
  { key: 'new_message', label: t('CLINICAL_CRM.STAGES.new_message') },
  { key: 'new_lead', label: t('CLINICAL_CRM.STAGES.new_lead') },
  {
    key: 'consultation_pending_payment',
    label: t('CLINICAL_CRM.STAGES.consultation_pending_payment'),
  },
  {
    key: 'consultation_paid',
    label: t('CLINICAL_CRM.STAGES.consultation_paid'),
  },
  { key: 'quote_sent', label: t('CLINICAL_CRM.STAGES.quote_sent') },
  {
    key: 'surgery_scheduled',
    label: t('CLINICAL_CRM.STAGES.surgery_scheduled'),
  },
  {
    key: 'surgery_completed',
    label: t('CLINICAL_CRM.STAGES.surgery_completed'),
  },
  {
    key: 'therapies_completed',
    label: t('CLINICAL_CRM.STAGES.therapies_completed'),
  },
  {
    key: 'controls_completed',
    label: t('CLINICAL_CRM.STAGES.controls_completed'),
  },
]);

const stageSelectOptions = computed(() =>
  stageDefinitions.value.map(stage => ({
    value: stage.key,
    label: stage.label,
  }))
);

// The empty choice stays a selectable option rather than the primitive's
// `placeholder`, which renders disabled. Clearing the procedure or the owner
// is a real action here: `updateDetails` maps the empty value back to null.
const procedureSelectOptions = computed(() => [
  { value: '', label: t('CLINICAL_CRM.NO_PROCEDURE') },
  ...procedures.value.map(procedure => ({
    value: procedure.id,
    label: procedure.name,
  })),
]);

const ownerSelectOptions = computed(() => [
  { value: '', label: t('CLINICAL_CRM.UNASSIGNED') },
  ...(agents.value ?? []).map(agent => ({
    value: agent.id,
    label: agent.name,
  })),
]);

const rememberFollowUp = () => {
  savedFollowUp.value = {
    nextAction: opportunity.value?.nextAction || '',
    nextActionAt: opportunity.value?.nextActionAt || '',
  };
};

const fetchOpportunity = async () => {
  isLoading.value = true;
  try {
    const { data } = await ClinicalCrmAPI.getOpportunities({
      contact_id: props.contactId,
      status: 'open',
    });
    const record = data.payload[0];
    opportunity.value = record
      ? {
          ...record,
          procedureId: record.procedure?.id || '',
          ownerId: record.owner?.id || '',
          nextActionAt: toDatetimeLocal(record.nextActionAt),
        }
      : null;
    rememberFollowUp();
  } finally {
    isLoading.value = false;
  }
};

const createOpportunity = async () => {
  isLoading.value = true;
  try {
    await ClinicalCrmAPI.createOpportunity({ contact_id: props.contactId });
    await fetchOpportunity();
    useAlert(t('CLINICAL_CRM.CONTACT_PANEL.CREATED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CLINICAL_CRM.DETAILS.UPDATE_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const updateStage = async () => {
  if (!opportunity.value) return;
  try {
    const { data } = await ClinicalCrmAPI.updateStage(
      opportunity.value.id,
      opportunity.value.stage,
      'contact_details'
    );
    opportunity.value.stageColor = data.stageColor;
    useAlert(t('CLINICAL_CRM.DETAILS.UPDATED'));
  } catch (error) {
    await fetchOpportunity();
    useAlert(
      error?.response?.data?.error || t('CLINICAL_CRM.DETAILS.UPDATE_ERROR')
    );
  }
};

const updateDetails = async () => {
  if (!opportunity.value) return;
  try {
    await ClinicalCrmAPI.updateOpportunity(opportunity.value.id, {
      procedure_id: opportunity.value.procedureId || null,
      owner_id: opportunity.value.ownerId || null,
      next_action: opportunity.value.nextAction || null,
      next_action_at: opportunity.value.nextActionAt || null,
    });
    await fetchOpportunity();
    useAlert(t('CLINICAL_CRM.DETAILS.UPDATED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CLINICAL_CRM.DETAILS.UPDATE_ERROR')
    );
  }
};

const saveFollowUpIfChanged = () => {
  if (!opportunity.value) return;
  const { nextAction, nextActionAt } = savedFollowUp.value;
  const changed =
    (opportunity.value.nextAction || '') !== nextAction ||
    (opportunity.value.nextActionAt || '') !== nextActionAt;
  if (!changed) return;
  updateDetails();
};

onMounted(async () => {
  const [{ data }] = await Promise.all([
    ClinicalCrmAPI.getProcedures(),
    store.dispatch('agents/get'),
  ]);
  procedures.value = data;
  await fetchOpportunity();
});

watch(
  () => props.contactId,
  () => fetchOpportunity()
);
</script>

<template>
  <section class="w-full p-4 border rounded-xl border-n-weak bg-n-alpha-1">
    <div class="flex items-center justify-between gap-3 mb-4">
      <h4 class="text-sm font-semibold text-n-slate-12">
        {{ t('CLINICAL_CRM.CONTACT_PANEL.TITLE') }}
      </h4>
      <span
        v-if="opportunity"
        class="px-2 py-1 text-xs rounded-full bg-n-alpha-2 text-n-slate-11"
      >
        {{
          opportunity.sourceType === 'meta'
            ? t('CLINICAL_CRM.FILTERS.META')
            : t('CLINICAL_CRM.FILTERS.ORGANIC')
        }}
      </span>
    </div>

    <div v-if="isLoading" class="text-sm text-n-slate-11">
      {{ t('CLINICAL_CRM.LOADING') }}
    </div>

    <div v-else-if="opportunity" class="grid gap-3">
      <label class="grid gap-1 text-xs text-n-slate-11">
        <span class="flex items-center gap-2">
          <span
            data-test-id="crm-stage-color"
            class="size-2.5 rounded-full"
            :class="stageColorClasses(opportunity.stageColor).dot"
          />
          {{ t('CLINICAL_CRM.DETAILS.STAGE') }}
        </span>
        <Select
          v-model="opportunity.stage"
          :options="stageSelectOptions"
          @change="updateStage"
        />
      </label>

      <label class="grid gap-1 text-xs text-n-slate-11">
        {{ t('CLINICAL_CRM.DETAILS.PROCEDURE') }}
        <Select
          v-model="opportunity.procedureId"
          :options="procedureSelectOptions"
          @change="updateDetails"
        />
      </label>

      <label class="grid gap-1 text-xs text-n-slate-11">
        {{ t('CLINICAL_CRM.DETAILS.OWNER') }}
        <Select
          v-model="opportunity.ownerId"
          :options="ownerSelectOptions"
          @change="updateDetails"
        />
      </label>

      <label class="grid gap-1 text-xs text-n-slate-11">
        <span class="flex items-center justify-between gap-2">
          {{ t('CLINICAL_CRM.DETAILS.NEXT_ACTION_AT') }}
          <span
            v-if="isOverdue"
            class="px-1.5 py-0.5 rounded-md bg-n-ruby-3 text-n-ruby-11"
          >
            {{ t('CLINICAL_CRM.WORK.OVERDUE') }}
          </span>
        </span>
        <Input
          v-model="opportunity.nextActionAt"
          type="datetime-local"
          @blur="saveFollowUpIfChanged"
        />
      </label>

      <label class="grid gap-1 text-xs text-n-slate-11">
        {{ t('CLINICAL_CRM.DETAILS.NEXT_ACTION') }}
        <TextArea
          v-model="opportunity.nextAction"
          auto-height
          min-height="3rem"
          max-height="9rem"
          @blur="saveFollowUpIfChanged"
        />
      </label>

      <div v-if="campaignLabel" class="grid gap-1 text-xs text-n-slate-11">
        {{ t('CLINICAL_CRM.DETAILS.CAMPAIGN') }}
        <span
          class="text-sm break-words text-n-slate-12"
          :title="opportunity.metaAdId || campaignLabel"
        >
          {{ campaignLabel }}
        </span>
      </div>
    </div>

    <div v-else class="grid gap-3">
      <p class="text-sm text-n-slate-11">
        {{ t('CLINICAL_CRM.CONTACT_PANEL.EMPTY') }}
      </p>
      <Button
        :label="t('CLINICAL_CRM.CONTACT_PANEL.CREATE')"
        size="sm"
        @click="createOpportunity"
      />
    </div>
  </section>
</template>
